import { Component, OnInit } from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "primeng/api";
import { VenueService } from "../../../services/venues/venue.service";
import { AddCapacityTemplateFormComponent } from "../add-capacity-template-form/add-capacity-template-form.component";
import { CreateOrEditCapacityModel } from "../../../models/tables/create-or-edit-capacity.model";
import { CreateCapacityForVenuesComponent } from "../create-capacity-for-venues/create-capacity-for-venues.component";
import { CapacityWarningsDialogComponent } from "../capacity-warnings-dialog/capacity-warnings-dialog.component";
import * as moment from "moment";
import { ChooseCapacityTypeDialogComponent } from "../choose-capacity-type-dialog/choose-capacity-type-dialog.component";

@Component({
  selector: 'app-list-edit-capacity-templates',
  templateUrl: './list-edit-capacity-templates.component.html',
  styleUrls: ['./list-edit-capacity-templates.component.scss']
})
export class ListEditCapacityTemplatesComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public errorModel: { [k: string]: any } = {};
  public spinner: boolean = false;
  public requiredFieldErrorMessage = "INFO.REQUIRED_FIELD";
  public venueId: string;
  public capacityTemplates: Array<CreateOrEditCapacityModel> = new Array<CreateOrEditCapacityModel>();
  //#endregion

  //#region Constructor
  constructor(private dialogService: DialogService,
              private translate: TranslateService,
              private ref: DynamicDialogRef,
              private config: DynamicDialogConfig,
              private venueService: VenueService,
              private messageService: MessageService) {
  }

  ngOnInit(): void {
    if (this.config.data != undefined && this.config.data.id) {
      this.venueId = this.config.data.id;
      this.loadCapacityTemplatesForVenue();
    }
  }
  //#endregion

  //#region Public Methods
  public loadCapacityTemplatesForVenue(): void {
    this.spinner = true;
    this.venueService.listCapacityTemplates(this.venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.capacityTemplates = response;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public updateCapacityDetails(model: CreateOrEditCapacityModel): void {
    const ref = this.dialogService.open(AddCapacityTemplateFormComponent, {
      data: {
        id: this.venueId,
        capacityConfig: JSON.parse(JSON.stringify(model))
      },
      header: this.translate.instant('VENUE.ADD_CAPACITY'),
      width: '650px',
      styleClass: 'create_capacity_template_dialog w_300_px_mbl',
      closable: false
    });

    ref.onClose.subscribe((item: any) => {
      if (item.error?.StatusCode === -401) {
          const ref1 = this.dialogService.open(CapacityWarningsDialogComponent, {
            data: {
              id: this.venueId,
              message: this.translate.instant('VENUE.CAPACITY_WARNING_1_MESSAGE')
            },
            header: this.translate.instant('VENUE.WARNING'),
            styleClass: 'dialog_long center-header',
            closable: false
          });

          ref1.onClose.subscribe((item: any) => {
            if (item) {
              this.chooseAndOpenAddCapacityDialog();
            }
          });
        } else if (item) {
          this.loadCapacityTemplatesForVenue();
        }
    });

  }

  public deleteCapacityTemplate(id: string): void {
    this.spinner = true;
    this.venueService.deleteCapacityTemplate(id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.loadCapacityTemplatesForVenue();
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public cancel(): void {
    this.ref.close(true);
  }

  public chooseAndOpenAddCapacityDialog(): void {
    if (!this.capacityTemplates.find(x => x.isDefault)) {
      this.openChooseTemplateTypeDialog();
    } else {
      this.openAddCapacityTemplateDialog();
    }
  }

  public openChooseTemplateTypeDialog(): void {
    const ref = this.dialogService.open(ChooseCapacityTypeDialogComponent, {
      data: {
        venueId: this.venueId,
        fromAddVenue: false
      },
      header: this.translate.instant('VENUE.CREATE_CAPACITY'),
      width: '335px'
    });
    ref.onClose.subscribe((item: any) => {
      this.openWarnings(item);
    });
  }

  public openAddCapacityTemplateDialog(): void {
    const ref = this.dialogService.open(AddCapacityTemplateFormComponent, {
      data: {
        id: this.venueId
      },
      header: this.translate.instant('VENUE.ADD_CAPACITY'),
      width: '650px',
      styleClass: 'create_capacity_template_dialog w_300_px_mbl',
      closable: false
    });

    ref.onClose.subscribe((item: any) => {
      this.openWarnings(item);
    });
  }

  public openWarnings(item: any): void {
    if (item.error?.StatusCode === -401) {
      const ref1 = this.dialogService.open(CapacityWarningsDialogComponent, {
        data: {
          id: this.venueId,
          message: this.translate.instant('VENUE.CAPACITY_WARNING_1_MESSAGE')
        },
        header: this.translate.instant('VENUE.WARNING'),
        styleClass: 'dialog_long center-header',
        closable: false
      });

      ref1.onClose.subscribe((item: any) => {
        if (item) {
          this.chooseAndOpenAddCapacityDialog();
        }
      });
    } else if (item) {
      if (this.capacityTemplates.length === 1 && this.capacityTemplates[0].isDefault) {
        const ref1 = this.dialogService.open(CapacityWarningsDialogComponent, {
          data: {
            id: this.venueId,
            message: this.translate.instant('VENUE.CAPACITY_WARNING_2_MESSAGE') + item.name + this.translate.instant('VENUE.CAPACITY_WARNING_3_MESSAGE') + moment(item.startDate).format('DD.MM.yyyy')
              + this.translate.instant('VENUE.TO') +  moment(item.endDate).format('DD.MM.yyyy') +
              ' (' + item.startTime + '-' + item.endTime + ').'
          },
          header: this.translate.instant('VENUE.WARNING'),
          styleClass: 'dialog_long center-header',
          closable: false
        });

        ref1.onClose.subscribe((item: any) => {
          if (item) {
            this.chooseAndOpenAddCapacityDialog();
          }
        });
      }
      this.loadCapacityTemplatesForVenue();
    }
  }

  public editCapacity(capacity: any): void {
    const ref = this.dialogService.open(CreateCapacityForVenuesComponent, {
      data: {
        id: this.venueId,
        capacityConfig: capacity
      },
      width: '1000px',
      styleClass: '',
      header: capacity.name
    });
    ref.onClose.subscribe((item: any) => {
      this.loadCapacityTemplatesForVenue();
      if (item) {
        this.messageService.add({severity:'success', summary: this.translate.instant('VENUE.TABLE_CAPACITY_UPDATED'), life: 3000});
      }
    });
  }

  public parseTime(rangeTime: string): Date {
    const [hours, minutes] = rangeTime.split(':');
    const dateObj = new Date();
    dateObj.setHours(parseInt(hours), parseInt(minutes));
    return dateObj;
  }
  //#endregion
}
